import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { cn } from "../../lib/utils";

/* Aceternity tracing-beam, adapted for BookingFlows — a brand-blue beam that
   traces down the left rail as the wrapped sections scroll by. Content is
   left untouched; the beam hides below lg. */
export const TracingBeam = ({ children, className }) => {
  const ref = useRef(null);
  const contentRef = useRef(null);
  const [svgHeight, setSvgHeight] = useState(0);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start center", "end center"],
  });

  useEffect(() => {
    const measure = () => {
      if (contentRef.current) setSvgHeight(contentRef.current.offsetHeight);
    };
    measure();
    const t = setTimeout(measure, 1200);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
    };
  }, []);

  const y1 = useSpring(useTransform(scrollYProgress, [0, 0.8], [50, svgHeight]), {
    stiffness: 500,
    damping: 90,
  });
  const y2 = useSpring(useTransform(scrollYProgress, [0, 1], [50, svgHeight - 200]), {
    stiffness: 500,
    damping: 90,
  });

  return (
    <div ref={ref} className={cn("relative w-full", className)} data-testid="tracing-beam-wrapper">
      <div
        className="absolute top-3 hidden lg:block left-[max(1rem,calc((100vw-80rem)/2-1.25rem))]"
        aria-hidden="true"
      >
        <motion.div
          transition={{ duration: 0.2, delay: 0.5 }}
          animate={{ boxShadow: "rgba(37, 99, 235, 0.25) 0px 3px 8px" }}
          className="border-netural-200 ml-[3px] flex h-4 w-4 items-center justify-center rounded-full border bg-white shadow-sm"
        >
          <motion.div
            transition={{ duration: 0.2, delay: 0.5 }}
            className="h-2 w-2 rounded-full border border-blue-500 bg-blue-100"
          />
        </motion.div>
        <svg
          viewBox={`0 0 20 ${svgHeight}`}
          width="20"
          height={svgHeight}
          className="ml-1 block"
          aria-hidden="true"
        >
          <path d={`M 1 0 V ${svgHeight}`} fill="none" stroke="#E5E7EB" strokeOpacity="0.6" />
          <motion.path
            d={`M 1 0 V ${svgHeight}`}
            fill="none"
            stroke="url(#bf-tracing-gradient)"
            strokeWidth="2"
          />
          <defs>
            <motion.linearGradient
              id="bf-tracing-gradient"
              gradientUnits="userSpaceOnUse"
              x1="0"
              x2="0"
              y1={y1}
              y2={y2}
            >
              <stop stopColor="#2563EB" stopOpacity="0" />
              <stop stopColor="#2563EB" />
              <stop offset="0.325" stopColor="#06B6D4" />
              <stop offset="1" stopColor="#38BDF8" stopOpacity="0" />
            </motion.linearGradient>
          </defs>
        </svg>
      </div>
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

export default TracingBeam;
